<template>
    <Head title="Sign In" />

    <div class="space-y-6">
            <!-- Header -->
            <div class="text-center">
                <h2 class="text-3xl font-bold text-secondary-900 dark:text-white">
                    Welcome back
                </h2>
                <p class="mt-2 text-sm text-secondary-600 dark:text-secondary-400">
                    Sign in to your account to continue
                </p>
            </div>

            <!-- Global Error Message (Account Locked, etc.) -->
            <div
                v-if="errorMessage"
                class="bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 text-danger-800 dark:text-danger-200 px-4 py-3 rounded-lg"
                role="alert"
            >
                <div class="flex items-start">
                    <svg class="w-5 h-5 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                    </svg>
                    <div class="flex-1">
                        <p class="font-medium">{{ errorMessage }}</p>
                        <p v-if="lockedUntil" class="text-sm mt-1">
                            Please try again after {{ formattedLockTime }}.
                        </p>
                    </div>
                </div>
            </div>

            <!-- Login Form -->
            <form @submit.prevent="submit" class="space-y-5" novalidate>
                <!-- Email Field -->
                <div>
                    <label
                        for="email"
                        class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2"
                    >
                        Email address
                    </label>
                    <div class="relative">
                        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <svg class="h-5 w-5 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                            </svg>
                        </div>
                        <input
                            id="email"
                            ref="emailInput"
                            v-model="form.email"
                            type="email"
                            autocomplete="email"
                            required
                            :class="[
                                'block w-full pl-10 pr-3 py-3 border rounded-lg text-secondary-900 dark:text-white placeholder-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors',
                                form.errors.email
                                    ? 'border-danger-300 dark:border-danger-600 bg-danger-50 dark:bg-danger-900/20'
                                    : 'border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-700'
                            ]"
                            placeholder="you@example.com"
                            @blur="validateEmail"
                        />
                    </div>
                    <p
                        v-if="form.errors.email"
                        class="mt-2 text-sm text-danger-600 dark:text-danger-400 flex items-center"
                    >
                        <svg class="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                        </svg>
                        {{ form.errors.email }}
                    </p>
                </div>

                <!-- Password Field -->
                <div>
                    <div class="flex items-center justify-between mb-2">
                        <label
                            for="password"
                            class="block text-sm font-medium text-secondary-700 dark:text-secondary-300"
                        >
                            Password
                        </label>
                        <Link
                            href="/forgot-password"
                            class="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
                        >
                            Forgot password?
                        </Link>
                    </div>
                    <div class="relative">
                        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <svg class="h-5 w-5 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                        </div>
                        <input
                            id="password"
                            ref="passwordInput"
                            v-model="form.password"
                            :type="showPassword ? 'text' : 'password'"
                            autocomplete="current-password"
                            required
                            :class="[
                                'block w-full pl-10 pr-12 py-3 border rounded-lg text-secondary-900 dark:text-white placeholder-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors',
                                form.errors.password
                                    ? 'border-danger-300 dark:border-danger-600 bg-danger-50 dark:bg-danger-900/20'
                                    : 'border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-700'
                            ]"
                            placeholder="Enter your password"
                            @blur="validatePassword"
                        />
                        <button
                            type="button"
                            @click="togglePasswordVisibility"
                            class="absolute inset-y-0 right-0 pr-3 flex items-center text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-300 transition-colors"
                            :aria-label="showPassword ? 'Hide password' : 'Show password'"
                        >
                            <!-- Eye Icon (Show) -->
                            <svg v-if="!showPassword" class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            <!-- Eye Off Icon (Hide) -->
                            <svg v-else class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                            </svg>
                        </button>
                    </div>
                    <p
                        v-if="form.errors.password"
                        class="mt-2 text-sm text-danger-600 dark:text-danger-400 flex items-center"
                    >
                        <svg class="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                        </svg>
                        {{ form.errors.password }}
                    </p>
                </div>

                <!-- Remember Me -->
                <div class="flex items-center">
                    <input
                        id="remember"
                        v-model="form.remember"
                        type="checkbox"
                        class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 dark:border-secondary-600 rounded cursor-pointer"
                    />
                    <label
                        for="remember"
                        class="ml-2 block text-sm text-secondary-700 dark:text-secondary-300 cursor-pointer select-none"
                    >
                        Remember me for 30 days
                    </label>
                </div>

                <!-- Submit Button -->
                <button
                    type="submit"
                    :disabled="form.processing || !isFormValid"
                    class="w-full flex justify-center items-center px-4 py-3 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                    <!-- Loading Spinner -->
                    <svg
                        v-if="form.processing"
                        class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                    >
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span v-if="form.processing">Signing in...</span>
                    <span v-else>Sign in</span>
                </button>
            </form>

            <!-- Divider (only show in SaaS mode) -->
            <div v-if="$page.props.deployment?.features?.registrationEnabled" class="relative">
                <div class="absolute inset-0 flex items-center">
                    <div class="w-full border-t border-secondary-300 dark:border-secondary-600"></div>
                </div>
                <div class="relative flex justify-center text-sm">
                    <span class="px-2 bg-white dark:bg-secondary-800 text-secondary-500 dark:text-secondary-400">
                        Don't have an account?
                    </span>
                </div>
            </div>

            <!-- Register Link (only show in SaaS mode) -->
            <div v-if="$page.props.deployment?.features?.registrationEnabled" class="text-center">
                <Link
                    href="/register"
                    class="inline-flex items-center text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
                >
                    Create a free account
                    <svg class="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                </Link>
            </div>
        </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import { Head, Link, router, useForm } from '@inertiajs/vue3';
import axios from 'axios';

// Define props (server-side errors, flash messages)
const props = defineProps({
    errors: {
        type: Object,
        default: () => ({}),
    },
    status: {
        type: String,
        default: null,
    },
    lockedUntil: {
        type: String,
        default: null,
    },
});

// Form state using Inertia's useForm
const form = useForm({
    email: '',
    password: '',
    remember: false,
});

// UI state
const showPassword = ref(false);
const emailInput = ref(null);
const passwordInput = ref(null);

// Error message (for global errors like account locked)
const errorMessage = computed(() => {
    if (props.status === 'account_locked') {
        return 'Your account has been locked due to multiple failed login attempts.';
    }
    if (props.status === 'email_not_verified') {
        return 'Please verify your email address before signing in.';
    }
    if (form.errors && form.errors.general) {
        return form.errors.general;
    }
    return null;
});

// Format locked until time
const formattedLockTime = computed(() => {
    if (!props.lockedUntil) return '';

    const lockedDate = new Date(props.lockedUntil);
    const now = new Date();
    const diffMs = lockedDate - now;
    const diffMins = Math.ceil(diffMs / 60000);

    if (diffMins <= 0) return 'now';
    if (diffMins === 1) return '1 minute';
    return `${diffMins} minutes`;
});

// Validate email format
const validateEmail = () => {
    if (!form.email) {
        form.errors.email = 'Email is required';
        return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
        form.errors.email = 'Please enter a valid email address';
        return false;
    }

    // Clear error if valid
    delete form.errors.email;
    return true;
};

// Validate password
const validatePassword = () => {
    if (!form.password) {
        form.errors.password = 'Password is required';
        return false;
    }

    if (form.password.length < 6) {
        form.errors.password = 'Password must be at least 6 characters';
        return false;
    }

    // Clear error if valid
    delete form.errors.password;
    return true;
};

// Check if form is valid
const isFormValid = computed(() => {
    return form.email && form.password && form.email.length > 0 && form.password.length > 0;
});

// Toggle password visibility
const togglePasswordVisibility = () => {
    showPassword.value = !showPassword.value;

    // Keep focus on password input
    nextTick(() => {
        if (passwordInput.value) {
            passwordInput.value.focus();
        }
    });
};

// Submit login form
const submit = async () => {
    // Clear previous errors
    form.clearErrors();

    // Client-side validation
    const emailValid = validateEmail();
    const passwordValid = validatePassword();

    if (!emailValid || !passwordValid) {
        return;
    }

    // Set form as processing
    form.processing = true;

    try {
        // Submit form to API endpoint
        const response = await axios.post('/api/auth/login', {
            email: form.email,
            password: form.password,
            remember: form.remember,
        });

        // Handle successful response
        const data = response.data;

        // Scenario 1: Requires 2FA
        if (data.requires_2fa && data.redirect_url) {
            window.location.href = data.redirect_url;
            return;
        }

        // Scenario 2: Requires company selection
        if (data.requires_company_selection) {
            router.visit('/select-company');
            return;
        }

        // Scenario 3: Direct login
        // Check if server provided a redirect_to URL (e.g., for first-time company setup)
        if (data.redirect_to) {
            // Use full page reload for session sync after Auth::login()
            window.location.href = data.redirect_to;
            return;
        }

        // Default: redirect to dashboard
        // Use full page reload to ensure session cookie is synced before user can interact
        window.location.href = '/dashboard';
    } catch (error) {
        // Handle error responses
        if (error.response) {
            const errors = error.response.data.errors || {};
            const message = error.response.data.message;

            // Set validation errors
            if (errors.email) {
                form.errors.email = Array.isArray(errors.email) ? errors.email[0] : errors.email;
            }
            if (errors.password) {
                form.errors.password = Array.isArray(errors.password) ? errors.password[0] : errors.password;
            }

            // Set general error message
            if (message) {
                form.errors.general = message;
            }

            // Focus on first field with error
            nextTick(() => {
                if (form.errors.email && emailInput.value) {
                    emailInput.value.focus();
                } else if (form.errors.password && passwordInput.value) {
                    passwordInput.value.focus();
                }
            });
        } else {
            // Network error or other issues
            form.errors.general = 'An error occurred. Please try again.';
        }
    } finally {
        // Always clear password and reset processing state
        form.password = '';
        form.processing = false;
    }
};

// Keyboard shortcuts
const handleKeyboard = (event) => {
    // Submit on Enter (if not already in form)
    if (event.key === 'Enter' && document.activeElement.tagName !== 'INPUT') {
        event.preventDefault();
        submit();
    }
};

// Initialize
onMounted(() => {
    // Focus email input on mount
    if (emailInput.value) {
        emailInput.value.focus();
    }

    // Add keyboard listeners
    window.addEventListener('keydown', handleKeyboard);

    // Cleanup
    return () => {
        window.removeEventListener('keydown', handleKeyboard);
    };
});
</script>

<style scoped>
/* Custom checkbox styling for dark mode */
input[type="checkbox"]:checked {
    background-color: #2563eb;
    border-color: #2563eb;
}

/* Focus ring for better accessibility */
input:focus,
button:focus {
    outline: 2px solid transparent;
    outline-offset: 2px;
}

/* Smooth transitions */
input,
button {
    transition: all 0.15s ease-in-out;
}
</style>
